import { Button, Icons } from '@cast/design-system';

import { ComputeResourceType } from '@cast/shared-types';

import { computeResourceUnitsMap } from 'constants/maps';
import { makeCustomMetricsLink } from 'features/cluster/workload-optimization/navigation';
import { useNavigate } from '@cast/infrastructure-organization';

import { ChartWidget } from '../ChartWidget';
import { jvmMetricToColor, jvmMetricToTitle } from '../constants';
import { JvmHeapChart } from './JvmHeapChart';
import { useWorkloadSelectedContainer } from '../_hooks/useWorkloadSelectedContainer';
import {
  CurrentRequestMetricBox,
  RecommendedMetricBox,
  RecommendedRequestMetricBoxes,
} from '../RecommendedRequestMetricBoxes';
import { UsageMetricsBoxes } from '../UsageMetricsBoxes';
import { useJvmChartData } from './useJvmChartData';

export const JvmChartSection = () => {
  const { container } = useWorkloadSelectedContainer();
  const {
    workload,
    timeZone,
    isFetching,
    error,
    refetch,
    chartData,
    toggleMetric,
    isMetricDisabled,
  } = useJvmChartData({ container });
  const navigate = useNavigate();

  return (
    <ChartWidget
      testId="jvm-section"
      title="JVM heap"
      titleAdornment={
        workload ? (
          <Button
            variant="text"
            size="lg"
            endIcon={<Icons.ArrowRight />}
            onClick={() =>
              navigate(
                makeCustomMetricsLink({ clusterId: workload.clusterId }) +
                  `?metrics-explorer=${encodeURIComponent(
                    JSON.stringify({
                      workload: workload.id,
                      container: container?.name,
                    })
                  )}`
              )
            }
          >
            Go to custom metrics
          </Button>
        ) : undefined
      }
      chartEl={
        <JvmHeapChart
          data={chartData.chartData}
          isLoading={isFetching || !workload}
          hasError={!!error && !isFetching}
          refetch={refetch}
          isMetricDisabled={isMetricDisabled}
          timeZone={timeZone}
        />
      }
      primaryMetricsEl={
        workload ? (
          <RecommendedRequestMetricBoxes
            showArrow
            currentEl={
              <CurrentRequestMetricBox
                title={jvmMetricToTitle.heapCommitted}
                acronym={computeResourceUnitsMap[ComputeResourceType.MEMORY]}
                markerColor={jvmMetricToColor.heapCommitted}
                from={chartData.metrics.heapCommitted}
                to={chartData.metrics.heapRecommended}
                isDisabled={isMetricDisabled('heapCommitted')}
                onClick={() => toggleMetric('heapCommitted')}
              />
            }
            recommendationEl={
              <RecommendedMetricBox
                type={ComputeResourceType.MEMORY}
                title={jvmMetricToTitle.heapRecommended}
                acronym={computeResourceUnitsMap[ComputeResourceType.MEMORY]}
                markerColor={jvmMetricToColor.heapRecommended}
                from={chartData.metrics.heapCommitted}
                to={chartData.metrics.heapRecommended}
                // jvm low conf. will be added in https://castai.atlassian.net/browse/WOOP-1421
                recommendation={undefined}
                resourceConfig={undefined}
                isReadOnly={workload.memory?.isReadOnly}
                isDisabled={isMetricDisabled('heapRecommended')}
                onClick={() => toggleMetric('heapRecommended')}
              />
            }
          />
        ) : null
      }
      secondaryMetricsEl={
        workload ? (
          <UsageMetricsBoxes
            tight
            unit="GiB"
            metricsBoxes={['heapUsed']}
            titleMap={jvmMetricToTitle}
            colorMap={jvmMetricToColor}
            summary={chartData.metrics}
            toggleMetricsBox={toggleMetric}
            isMetricDisabled={isMetricDisabled}
          />
        ) : undefined
      }
    />
  );
};
